import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { BaseModal } from "@/components/modal/base-modal";
import { FilledButton } from "@/components/ui/buttons";
import { useGetPlan } from "@/hooks/requests/plan/use-get-plan";
import { useAddMealSlot } from "@/hooks/requests/plan/use-add-meal-slot";
import { useGetYourFood } from "@/hooks/requests/your-food/use-get-your-food";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { format, isAfter, startOfDay } from "date-fns";
import { memo, useEffect, useMemo, useState } from "react";
import { getPlanDayEntry, getPlanSelectorDays } from "../../utils/plan-week-data";
import { DaySelector } from "../day-selector";
import { AddToPlanMealItem } from "./add-to-plan-meal-item";

interface AddToPlanModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  meal: Meal;
}

/**
 * Modal for adding a meal to the user's plan
 * Shows a day selector and meal slots for the selected day
 */
export const AddToPlanModal = memo(function AddToPlanModal({
  isOpen,
  onDismiss,
  meal,
}: AddToPlanModalProps) {
  const { plan, isLoading } = useGetPlan();
  const { addMealSlotAsync, isLoading: isUpdating } = useAddMealSlot();
  const { yourFoodMealsSet } = useGetYourFood();
  const { showSuccess } = useToast();

  const [selectedDay, setSelectedDay] = useState<string>("");
  const [selectedDateKey, setSelectedDateKey] = useState<string>("");
  const days = useMemo(() => getPlanSelectorDays(plan), [plan]);

  useEffect(() => {
    if (!selectedDay && !isLoading && plan) {
      const now = new Date();
      const today = startOfDay(now);
      const nowDayOfWeek = format(now, "EEEE").toLowerCase();
      const todayDateKey = format(today, "yyyy-MM-dd");

      const todayEntry = days.find((entry) => entry.dateKey === todayDateKey);
      if (todayEntry) {
        setSelectedDay(todayEntry.day);
        setSelectedDateKey(todayEntry.dateKey);
        return;
      }

      const matchingDayEntry = getPlanDayEntry(plan, nowDayOfWeek);
      if (matchingDayEntry && !isAfter(today, startOfDay(matchingDayEntry.date))) {
        setSelectedDay(matchingDayEntry.day);
        setSelectedDateKey(matchingDayEntry.dateKey);
        return;
      }

      const firstFutureOrTodayEntry = days.find((entry) => !isAfter(today, startOfDay(entry.date)));
      if (firstFutureOrTodayEntry) {
        setSelectedDay(firstFutureOrTodayEntry.day);
        setSelectedDateKey(firstFutureOrTodayEntry.dateKey);
        return;
      }

      const fallbackEntry = days[0];
      if (fallbackEntry) {
        setSelectedDay(fallbackEntry.day);
        setSelectedDateKey(fallbackEntry.dateKey);
      }
    }
  }, [days, isLoading, plan, selectedDay]);

  const dayMeals = useMemo(() => {
    return getPlanDayEntry(plan, selectedDay, selectedDateKey)?.meals ?? [];
  }, [plan, selectedDateKey, selectedDay]);

  const handleDaySelect = (day: string, dateKey: string) => {
    setSelectedDay(day);
    setSelectedDateKey(dateKey);
  };

  const handleSelectMeal = async (mealTime: MealTime) => {
    if (!meal?.id) {
      return;
    }

    try {
      await addMealSlotAsync({
        day: selectedDay,
        mealTime,
        meal_id: meal.id,
        meal_name: meal.name ?? undefined,
        targetDate: selectedDateKey || undefined,
      });

      const mealTimeLabel = mealTime.toLowerCase();
      showSuccess(`Meal added to ${selectedDay} for ${mealTimeLabel}.`);
      onDismiss();
    } catch (error) {
      console.error("Failed to add meal to plan:", error);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={true}
      initialBreakpoint={1}
      breakpoints={[0, 0.6, 1]}
    >
      <div className="safe-area-botton flex flex-1 flex-col overflow-clip pt-6 pb-8">
        <h2 className={cn(textVariants.title2, "pb-3 text-center")}>Add meal to your plan</h2>

        <div className="px-4">
          {isLoading ? (
            <IonSkeletonText animated className="h-16 w-full rounded-2xl" />
          ) : (
            <DaySelector
              weekDays={days}
              selectedDateKey={selectedDateKey}
              onSelectDate={handleDaySelect}
              disablePastDays={true}
            />
          )}
        </div>

        <div className="no-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto pt-6">
          {isLoading ? (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <IonSkeletonText key={i} animated className="h-16 w-full rounded-2xl" />
              ))}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-3 px-4">
                <h3 className={cn(textVariants.bodySmall, "font-semibold text-gray-900")}>Meals</h3>

                <AddToPlanMealItem
                  mealTime={MealTime.BREAKFAST}
                  meals={dayMeals}
                  onSelect={handleSelectMeal}
                  isUpdating={isUpdating}
                  yourFoodMealsSet={yourFoodMealsSet}
                />

                <AddToPlanMealItem
                  mealTime={MealTime.LUNCH}
                  meals={dayMeals}
                  onSelect={handleSelectMeal}
                  isUpdating={isUpdating}
                  yourFoodMealsSet={yourFoodMealsSet}
                />

                <AddToPlanMealItem
                  mealTime={MealTime.DINNER}
                  meals={dayMeals}
                  onSelect={handleSelectMeal}
                  isUpdating={isUpdating}
                  yourFoodMealsSet={yourFoodMealsSet}
                />
              </div>

              {/* Snacks Section */}
              <div className="flex flex-col gap-3 px-4">
                <h3 className={cn(textVariants.bodySmall, "font-semibold text-gray-900")}>
                  Snacks
                </h3>

                <AddToPlanMealItem
                  mealTime={MealTime.SNACK}
                  meals={dayMeals}
                  onSelect={handleSelectMeal}
                  isUpdating={isUpdating}
                  yourFoodMealsSet={yourFoodMealsSet}
                  showAllSlots
                />
              </div>
            </>
          )}

          <div className="px-4">
            <FilledButton color="gray" onClick={onDismiss}>
              Cancel
            </FilledButton>
          </div>
        </div>
      </div>
    </BaseModal>
  );
});
